import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStripe } from "@stripe/react-stripe-js";

import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import depositSummaryStyles from "../AssignToProvider/PricingV2/InfoCard/InfoCard.module.scss";

import AuthService from "../../utilities/services/auth.service";

import {
  formatCurrency,
  emailPatternMatch,
  getPaymentOption,
  getProcessingFee,
} from "../../utilities/helpers";

import {
  createAdminDeposit,
  getMyOrganizations,
  getPaymentIntent,
} from "../../utilities/api";

import CardForm from "../AssignToProvider/CardForm";

import DepositAssignToForm from "./DepositAssignToForm";

import { useModal } from "../ModalProvider/ModalProvider";

import { Success, Error } from "../../components/ModalContent";

import { constants, fees } from "@blocktickets/shared-utils";

const { PAYMENT_TYPE, ORDER_SOURCE, PRODUCT_TYPE } = constants;

const DEPOSIT_METHODS = ["CARD", "CASH"];

function DepositSummaryClockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function depositChargeHeaderLabel(paymentMethodKey) {
  const key = String(paymentMethodKey || "").toUpperCase();
  if (key === "CASH") return "Amount to collect:";
  return "Amount to charge on card:";
}

function createSeatRow() {
  return {
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `seat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    section: "",
    row: "",
    seats: "",
  };
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Strapi REST / users-permissions often nests relations as
 * { data: { id, attributes } } while the admin session may store a flat shape.
 */
function unwrapStrapiEntity(rel) {
  if (rel == null) return null;
  if (rel.attributes != null && typeof rel.attributes === "object") {
    return { id: rel.id, ...rel.attributes };
  }
  const inner =
    rel.data != null
      ? Array.isArray(rel.data)
        ? rel.data[0]
        : rel.data
      : null;
  if (inner == null) return rel;
  return inner.attributes ? { id: inner.id, ...inner.attributes } : inner;
}

function getOrganizationFeeRulesFromOrg(org) {
  const ofc = unwrapStrapiEntity(org?.organization_fee_config);
  if (!ofc) return [];
  const raw = ofc.rules;
  if (Array.isArray(raw)) {
    return raw.map((r) => {
      if (!r || typeof r !== "object") return r;
      const pt = r.productType ?? r.product_type;
      return pt !== undefined ? { ...r, productType: pt } : r;
    });
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((r) => {
        if (!r || typeof r !== "object") return r;
        const pt = r.productType ?? r.product_type;
        return pt !== undefined ? { ...r, productType: pt } : r;
      });
    } catch {
      return [];
    }
  }
  return [];
}

function resolveOrganizationTaxRate(org) {
  return unwrapStrapiEntity(org?.taxRate) || null;
}

/** Reads combined rate from org relation (flat, nested Strapi, or snake_case). */
function pickEstimatedCombinedRateFromOrg(org) {
  if (!org || typeof org !== "object") return undefined;
  const tries = [
    org.taxRate,
    org.tax_rate,
    resolveOrganizationTaxRate(org.taxRate),
    resolveOrganizationTaxRate(org.tax_rate),
  ];
  for (const tr of tries) {
    if (!tr || typeof tr !== "object") continue;
    const raw =
      tr.estimatedCombinedRate ??
      tr.estimated_combined_rate ??
      tr.attributes?.estimatedCombinedRate ??
      tr.attributes?.estimated_combined_rate;
    if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
      return raw;
    }
  }
  return undefined;
}

/** Map stored combined rate to tax dollars (handles percent points vs decimal). */
function salesTaxFromCombinedRate(taxableBase, storedCombinedRate) {
  const n = Number(storedCombinedRate);
  if (
    !Number.isFinite(n) ||
    n <= 0 ||
    !Number.isFinite(taxableBase) ||
    taxableBase <= 0
  ) {
    return 0;
  }
  if (n <= 1) {
    return round2(taxableBase * n);
  }
  return round2(taxableBase * (n / 100));
}

function normalizeFeeRuleForCalculator(rule) {
  if (!rule || typeof rule !== "object") return rule;
  const method =
    typeof rule.method === "string" ? rule.method.toLowerCase() : rule.method;
  const type =
    typeof rule.type === "string" ? rule.type.toLowerCase() : rule.type;
  return { ...rule, method, type };
}

/** Mirrors server resolveDepositPricing for admin display. */
function computeDepositPriceSummary({
  depositBase,
  paymentMethodKey,
  organization,
  organizationFeeRules,
}) {
  const base = Number(depositBase) || 0;
  if (base <= 0) return null;

  const pm = String(paymentMethodKey || "CARD").toUpperCase();
  const processingFee = pm === "CASH" ? 0 : getProcessingFee("CARD", base);

  const rules = Array.isArray(organizationFeeRules) ? organizationFeeRules : [];
  const depositRules = rules.filter((r) => {
    const pt = String(r?.productType ?? r?.product_type ?? "").toLowerCase();
    return pt === String(PRODUCT_TYPE.DEPOSIT).toLowerCase();
  });
  let depositFee = 0;
  if (organization?.serviceFeeOnDeposits !== false) {
    const engineRules = depositRules.map(normalizeFeeRuleForCalculator);
    const applied = fees.calculateItemFees(
      {
        price: base,
        quantity: 1,
        source: ORDER_SOURCE.BOX_OFFICE,
        productType: PRODUCT_TYPE.DEPOSIT,
      },
      engineRules,
    );
    depositFee = applied.reduce((sum, f) => sum + f.value, 0);
  }

  const storedRate = pickEstimatedCombinedRateFromOrg(organization);

  const taxableBase = base + depositFee + processingFee;
  let salesTax = 0;
  const rateNum = Number(storedRate);
  const hasRate = Number.isFinite(rateNum) && rateNum > 0;
  if (
    hasRate &&
    organization?.taxOnDeposits !== false &&
    organization?.isTaxable !== false
  ) {
    salesTax = salesTaxFromCombinedRate(taxableBase, storedRate);
  }

  const totalCharge = round2(base + depositFee + processingFee + salesTax);

  const rateDisplay = Number(storedRate);
  return {
    depositBaseAmount: base,
    depositFee,
    processingFee,
    salesTax,
    totalCharge,
    taxRatePercent: Number.isFinite(rateDisplay) ? rateDisplay : 0,
  };
}

export default function DepositModalContent({
  show,
  closeDeposit,
  connectedAccountId,
  stripeAccountContext,
  onStripeAccountContextChange,
  onDepositRecorded,
}) {
  const stripe = useStripe();
  const { openModal, closeModal: closeOverlayModal } = useModal();

  const organizations = useMemo(() => AuthService.getOrg() || [], []);
  const paymentOrg = organizations?.[0];

  const [orgPricingSnapshot, setOrgPricingSnapshot] = useState(null);

  useEffect(() => {
    if (!show) {
      setOrgPricingSnapshot(null);
      return undefined;
    }
    const uuid = paymentOrg?.uuid;
    if (!uuid) return undefined;
    let cancelled = false;
    getMyOrganizations()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const match =
          list.find((o) => o?.uuid && String(o.uuid) === String(uuid)) ||
          list[0];
        if (match) setOrgPricingSnapshot(match);
      })
      .catch(() => {
        if (!cancelled) setOrgPricingSnapshot(null);
      });
    return () => {
      cancelled = true;
    };
  }, [show, paymentOrg?.uuid]);

  const feeConfigOrg = useMemo(() => {
    if (
      orgPricingSnapshot?.uuid &&
      paymentOrg?.uuid &&
      String(orgPricingSnapshot.uuid) === String(paymentOrg.uuid)
    ) {
      return orgPricingSnapshot;
    }
    if (!paymentOrg?.uuid) return paymentOrg;
    const match = organizations.find(
      (o) => o?.uuid && String(o.uuid) === String(paymentOrg.uuid),
    );
    return match || paymentOrg;
  }, [organizations, paymentOrg, orgPricingSnapshot]);

  const [step, setStep] = useState(0);

  const [assignTo, setAssignTo] = useState({
    organizationName: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: null,
  });
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isValidNumber, setIsValidNumber] = useState(true);
  const [noteText, setNoteText] = useState("");

  const [price, setPrice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [seatInfo, setSeatInfo] = useState([]);

  const [clientSecret, setClientSecret] = useState();
  const [initialClientSecret, setInitialClientSecret] = useState();
  const [card, setCard] = useState();
  const [isCardValid, setIsCardValid] = useState(false);
  const lastPaymentIntentKeyRef = useRef(null);
  const inFlightPaymentIntentKeyRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) return;
    setStep(0);
    setAssignTo({
      organizationName: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: null,
    });
    setPrice("");
    setPaymentMethod("");
    setSeatInfo([]);
    setNoteText("");
    setClientSecret();
    setInitialClientSecret();
    setIsCardValid(false);
    lastPaymentIntentKeyRef.current = null;
    inFlightPaymentIntentKeyRef.current = null;
  }, [show]);

  const hasValidDepositAmount = useMemo(() => {
    const n = Number(price);
    return Number.isFinite(n) && n > 0 && String(price).trim() !== "";
  }, [price]);

  useEffect(() => {
    if (hasValidDepositAmount) return;
    setPaymentMethod("");
    setClientSecret();
    setInitialClientSecret();
    setIsCardValid(false);
    lastPaymentIntentKeyRef.current = null;
    inFlightPaymentIntentKeyRef.current = null;
  }, [hasValidDepositAmount]);

  const depositPricing = useMemo(() => {
    if (!hasValidDepositAmount || !feeConfigOrg) return null;
    const rules = getOrganizationFeeRulesFromOrg(feeConfigOrg);
    const taxFlat = resolveOrganizationTaxRate(feeConfigOrg);
    const orgForPricing = {
      ...feeConfigOrg,
      ...(taxFlat ? { taxRate: taxFlat } : {}),
    };
    return computeDepositPriceSummary({
      depositBase: price,
      paymentMethodKey: paymentMethod || "CARD",
      organization: orgForPricing,
      organizationFeeRules: rules,
    });
  }, [hasValidDepositAmount, price, paymentMethod, feeConfigOrg]);

  const amountDue = useMemo(
    () =>
      depositPricing?.totalCharge != null
        ? Number(depositPricing.totalCharge) || 0
        : 0,
    [depositPricing],
  );

  const shouldRequestIntent = paymentMethod === "CARD" && amountDue > 0;

  const paymentIntentKey = useMemo(() => {
    if (!shouldRequestIntent) return null;
    return [
      paymentMethod,
      price,
      depositPricing?.totalCharge ?? "",
      paymentOrg?.uuid,
      assignTo?.email,
    ].join(":");
  }, [
    shouldRequestIntent,
    paymentMethod,
    price,
    depositPricing?.totalCharge,
    paymentOrg?.uuid,
    assignTo?.email,
  ]);

  const loadPaymentIntent = useCallback(
    (requestKey) => {
      if (!requestKey) return;
      if (
        inFlightPaymentIntentKeyRef.current === requestKey ||
        lastPaymentIntentKeyRef.current === requestKey
      ) {
        return;
      }
      inFlightPaymentIntentKeyRef.current = requestKey;
      setClientSecret();
      setInitialClientSecret();

      getPaymentIntent({
        deposit: {
          assignTo,
          depositBaseAmount: Number(price),
          organization: paymentOrg,
          paymentOption: getPaymentOption(paymentMethod),
          paymentMethod: PAYMENT_TYPE[paymentMethod],
        },
      })
        .then((res) => {
          if (res?.data?.skipPaymentIntent) {
            lastPaymentIntentKeyRef.current = requestKey;
            return;
          }
          const isDestinationCharge = Boolean(
            res?.data?.transfer_data?.destination,
          );
          const desiredStripeAccountContext = isDestinationCharge
            ? null
            : connectedAccountId;
          const didContextChange =
            desiredStripeAccountContext !== stripeAccountContext;

          if (typeof onStripeAccountContextChange === "function") {
            onStripeAccountContextChange(desiredStripeAccountContext);
          }

          if (didContextChange) {
            setClientSecret("");
            setTimeout(() => {
              setClientSecret(res?.data?.client_secret);
              setInitialClientSecret(res?.data?.client_secret);
            }, 50);
          } else {
            setClientSecret(res?.data?.client_secret);
            setInitialClientSecret(res?.data?.client_secret);
          }
          lastPaymentIntentKeyRef.current = requestKey;
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (inFlightPaymentIntentKeyRef.current === requestKey) {
            inFlightPaymentIntentKeyRef.current = null;
          }
        });
    },
    [
      assignTo,
      price,
      paymentOrg,
      paymentMethod,
      depositPricing?.processingFee,
      connectedAccountId,
      stripeAccountContext,
      onStripeAccountContextChange,
    ],
  );

  useEffect(() => {
    if (!shouldRequestIntent) {
      lastPaymentIntentKeyRef.current = null;
      inFlightPaymentIntentKeyRef.current = null;
      setClientSecret();
      return;
    }
    if (!paymentIntentKey) return;
    if (step !== 1) return;
    if (lastPaymentIntentKeyRef.current === paymentIntentKey) return;
    loadPaymentIntent(paymentIntentKey);
  }, [shouldRequestIntent, paymentIntentKey, step, loadPaymentIntent]);

  const handleAssignToChange = (field, value) => {
    setAssignTo((prev) => ({ ...prev, [field]: value }));
  };

  const updateSeatInfoRow = (id, field, value) => {
    setSeatInfo((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const addSeatInfoRow = () => {
    setSeatInfo((prev) => [...prev, createSeatRow()]);
  };

  const removeSeatInfoRow = (id) => {
    setSeatInfo((prev) => prev.filter((row) => row.id !== id));
  };

  const seatInfoPayload = useMemo(
    () =>
      seatInfo.map(({ section, row, seats }) => ({
        section: String(section ?? "").trim(),
        row: String(row ?? "").trim(),
        seats: String(seats ?? "").trim(),
      })),
    [seatInfo],
  );

  const stepIsValid = useMemo(() => {
    if (step === 0) {
      return (
        assignTo.firstName.trim() !== "" &&
        assignTo.lastName.trim() !== "" &&
        assignTo.email.trim() !== "" &&
        isValidEmail &&
        isValidNumber &&
        emailPatternMatch(assignTo.email)
      );
    }
    if (step === 1) {
      if (!paymentMethod || !hasValidDepositAmount) return false;
      if (paymentMethod === "CASH") return true;
      return Boolean(clientSecret) && isCardValid;
    }
    return false;
  }, [
    step,
    assignTo,
    isValidEmail,
    isValidNumber,
    price,
    paymentMethod,
    clientSecret,
    isCardValid,
    hasValidDepositAmount,
  ]);

  const openSuccess = () => {
    openModal(
      Success,
      {
        title: "Deposit recorded",
        description: `A deposit of ${formatCurrency(amountDue)} has been saved.`,
        onClose: () => {
          closeOverlayModal();
          closeDeposit();
        },
      },
      { className: "modal-md" },
    );
  };

  const openErr = (message) => {
    openModal(
      Error,
      {
        title: "Deposit failed",
        description: message,
        onClose: closeOverlayModal,
      },
      { className: "modal-md" },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 0 && stepIsValid) {
      setStep(1);
      return;
    }
    if (step === 1 && stepIsValid) {
      setIsSaving(true);
      try {
        let paymentIntentId = "";
        if (paymentMethod === "CARD") {
          if (!stripe || !clientSecret) {
            throw new Error("Payment is still loading. Please wait.");
          }
          const payload = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card },
          });
          if (payload?.error) throw new Error(payload.error.message);
          paymentIntentId = payload?.paymentIntent?.id || "";
        }

        await createAdminDeposit({
          organizationId: paymentOrg?.id,
          depositBaseAmount: Number(price),
          assignTo,
          paymentIntentId,
          paymentMethod: PAYMENT_TYPE[paymentMethod],
          paymentOption: getPaymentOption(paymentMethod),
          paymentType: "one-time",
          seatInfo: seatInfoPayload,
          processingFee: depositPricing?.processingFee ?? null,
          serviceFee: depositPricing?.depositFee ?? null,
          salesTax: depositPricing?.salesTax ?? null,
          noteText: noteText.trim() || null,
        });

        onDepositRecorded?.();
        openSuccess();
      } catch (err) {
        openErr(
          err?.message ||
            "The deposit could not be completed. Please try again.",
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const heading =
    step === 0
      ? {
          title: "Depositor information",
          desc: "Contact information for this deposit.",
        }
      : {
          title: "Deposit details",
          desc: "Amount, optional seat rows, and payment method (card or cash only).",
        };

  return (
    <Modal.Body className="d-flex-column">
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">{heading.title}</h1>
        <p className="mt-0 normal">{heading.desc}</p>
      </div>

      <Form onSubmit={handleSubmit} className="d-flex-column flex-grow-1">
        {step === 0 && (
          <DepositAssignToForm
            assignTo={assignTo}
            onAssignToChange={handleAssignToChange}
            noteText={noteText}
            onNoteChange={setNoteText}
            isValidEmail={isValidEmail}
            setIsValidEmail={setIsValidEmail}
            isValidNumber={isValidNumber}
            setIsValidNumber={setIsValidNumber}
          />
        )}
        {step === 1 && (
          <Stack gap={2}>
            <Form.Group controlId="depositAmount">
              <Form.Label>Deposit amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </Form.Group>
            {hasValidDepositAmount && depositPricing && (
              <Card className={`${depositSummaryStyles.wrap} card-with-border`}>
                <Card.Body className={depositSummaryStyles.cardBody}>
                  <div className={depositSummaryStyles.header}>
                    <DepositSummaryClockIcon
                      className={depositSummaryStyles.headerIcon}
                    />
                    <div className={depositSummaryStyles.headerText}>
                      {depositChargeHeaderLabel(paymentMethod || "CARD")}{" "}
                      <strong>
                        {formatCurrency(depositPricing.totalCharge)}
                      </strong>
                    </div>
                  </div>
                  <div className={depositSummaryStyles.summary}>
                    <div className={depositSummaryStyles.summaryTitle}>
                      Price Summary
                    </div>
                    <div className={depositSummaryStyles.rowGroup}>
                      <div className={depositSummaryStyles.row}>
                        <span>Deposit amount</span>
                        <span>
                          {formatCurrency(depositPricing.depositBaseAmount)}
                        </span>
                      </div>
                    </div>
                    <hr className={depositSummaryStyles.dividerDashed} />
                    <div className={depositSummaryStyles.rowGroup}>
                      <div className={depositSummaryStyles.row}>
                        <span>Deposit fee</span>
                        <span>{formatCurrency(depositPricing.depositFee)}</span>
                      </div>
                      <div className={depositSummaryStyles.row}>
                        <span>Sales Tax</span>
                        <span>{formatCurrency(depositPricing.salesTax)}</span>
                      </div>
                      <div className={depositSummaryStyles.row}>
                        <span>Processing Fee</span>
                        <span>
                          {formatCurrency(depositPricing.processingFee)}
                        </span>
                      </div>
                    </div>
                    <hr className={depositSummaryStyles.dividerSolid} />
                    <div className={depositSummaryStyles.footerRow}>
                      <span>Total</span>
                      <span>{formatCurrency(depositPricing.totalCharge)}</span>
                    </div>
                  </div>
                  {!paymentMethod ? (
                    <p className="text-muted small mb-0 px-3 pb-3">
                      Estimated total assumes card until you choose cash (no
                      processing fee).
                    </p>
                  ) : null}
                </Card.Body>
              </Card>
            )}
            <div>
              <Form.Label className="d-block mb-2">
                Seat info (optional)
              </Form.Label>
              {seatInfo.length === 0 && (
                <p className="text-muted small mb-2">
                  No seat rows yet. Use &quot;Add seat row&quot; to include
                  section, row, and seat number(s).
                </p>
              )}
              <Stack gap={2}>
                {seatInfo.map((rowItem, index) => (
                  <Row key={rowItem.id} className="g-2 align-items-end">
                    <Col xs={12} md={3}>
                      <Form.Group controlId={`deposit-section-${rowItem.id}`}>
                        <Form.Label className="small text-muted mb-1">
                          Section
                        </Form.Label>
                        <Form.Control
                          value={rowItem.section}
                          onChange={(e) =>
                            updateSeatInfoRow(
                              rowItem.id,
                              "section",
                              e.target.value,
                            )
                          }
                          placeholder="Section"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Group controlId={`deposit-row-${rowItem.id}`}>
                        <Form.Label className="small text-muted mb-1">
                          Row
                        </Form.Label>
                        <Form.Control
                          value={rowItem.row}
                          onChange={(e) =>
                            updateSeatInfoRow(rowItem.id, "row", e.target.value)
                          }
                          placeholder="Row"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Group controlId={`deposit-seat-${rowItem.id}`}>
                        <Form.Label className="small text-muted mb-1">
                          Seat number(s)
                        </Form.Label>
                        <Form.Control
                          value={rowItem.seats}
                          onChange={(e) =>
                            updateSeatInfoRow(
                              rowItem.id,
                              "seats",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. 12–14"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto" className="pb-md-2">
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeSeatInfoRow(rowItem.id)}
                        aria-label={`Remove seat row ${index + 1}`}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Stack>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={addSeatInfoRow}
              >
                Add seat row
              </Button>
            </div>
            <Form.Group controlId="depositPaymentMethod">
              <Form.Label>Payment method</Form.Label>
              <Form.Select
                value={paymentMethod}
                disabled={!hasValidDepositAmount}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="">
                  {hasValidDepositAmount
                    ? "Select payment method"
                    : "Enter deposit amount first"}
                </option>
                {DEPOSIT_METHODS.map((key) => (
                  <option key={key} value={key}>
                    {key === "CARD" ? "Card" : "Cash"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {paymentMethod === "CARD" && Boolean(clientSecret) && (
              <CardForm
                client_secret={clientSecret}
                initialClientSecret={initialClientSecret}
                setIsCardValid={setIsCardValid}
                setCard={setCard}
                setStripe={() => {}}
                selectedCard={undefined}
                setSelectedCard={() => {}}
              />
            )}
            {paymentMethod === "CARD" &&
              shouldRequestIntent &&
              !clientSecret && (
                <p className="text-muted small">Loading payment form…</p>
              )}
          </Stack>
        )}

        <Stack
          direction="horizontal"
          gap={2}
          className="mt-3 justify-content-between"
        >
          <div>
            {step > 0 && (
              <Button
                variant="outline-light"
                type="button"
                onClick={handleBack}
              >
                Back
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={!stepIsValid || isSaving}
            variant="primary"
          >
            {step === 1
              ? isSaving
                ? "Saving…"
                : "Submit deposit"
              : "Continue"}
          </Button>
        </Stack>
      </Form>
    </Modal.Body>
  );
}
